import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { CreateUpdateDepartment } from "../departmentsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import {
    CompanyAdminDepartmentsQuery,
    DepartmentForCompanyResponse,
    fetchDepartmentForCompany,
    updateDepartmentForCompany,
} from "../departmentsApi"
import { CreateUpdateDepartmentForm } from "../components/CreateUpdateDepartmentForm"
import { COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"

const selectDepartment = (data: DepartmentForCompanyResponse) => {
    const department = data.department
    if (!department) return null
    const { id, history, ...initialValues } = department

    return { initialValues, history }
}

const Department = forwardRef<FormikProps<CreateUpdateDepartment>>((props, ref) => {
    const { departmentId } = useParams()
    const navigate = useNavigate()

    const departmentResult = useApiQuery(fetchDepartmentForCompany, {
        queryName: CompanyAdminDepartmentsQuery.DETAILS,
        dependencies: {
            departmentId: departmentId || "",
        },
        enabled: Boolean(departmentId),
        select: selectDepartment,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateDepartment } = useApiMutation(updateDepartmentForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminDepartmentsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(CompanyAdminDepartmentsQuery.DETAILS, { departmentId: params.departmentId }),
            })
            navigate(COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateDepartmentForm
                ref={ref}
                initialValues={departmentResult.data?.initialValues}
                history={departmentResult.data?.history}
                onSubmit={(data) => {
                    departmentId && callUpdateDepartment({ department: data, departmentId: departmentId })
                }}
            />
        </>
    )
})

export function EditDepartment() {
    const departmentFormRef = useRef<FormikProps<CreateUpdateDepartment>>(null)

    const handleSaveClick = () => {
        if (departmentFormRef.current) {
            departmentFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="edit-department-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_EDIT}
                    title="Edit department"
                    rightElement={
                        <LocalizedStrict id="edit-department-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-departments-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE}>
                                Departments
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-departments-edit-title">
                            <Breadcrumb>Edit department</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Department ref={departmentFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
